import { create } from "zustand";
import { persist } from "zustand/middleware";
import { colors, palette, writeFigmaColorVars } from "@/lib/colors";
import i18n from "@/lib/i18n";
import { typography, writeTypographyVars } from "@/lib/typography";
import useAuthStore from "./use-auth-store";
import { getLanguageFromURL, getUILanguageName, getUILocale, UI_LANGUAGES } from "@/i18n/languages";

// ===== UI Preferences Types =====
export type ThemeMode = "light" | "dark" | "system";

// Supported languages as expected by API
export const SUPPORTED_LANGUAGES = [
  "Amharic",
  "Arabic",
  "Bengali",
  "Burmese",
  "Dutch",
  "English",
  "French",
  "German",
  "Gujarati",
  "Hausa",
  "Hindi",
  "Indonesian",
  "Italian",
  "Japanese",
  "Kannada",
  "Korean",
  "Malay",
  "Malayalam",
  "Mandarin Chinese",
  "Marathi",
  "Odia",
  "Pashto",
  "Persian",
  "Polish",
  "Portuguese",
  "Punjabi",
  "Romanian",
  "Russian",
  "Sindhi",
  "Spanish",
  "Swahili",
  "Tamil",
  "Telugu",
  "Thai",
  "Turkish",
  "Ukrainian",
  "Urdu",
  "Vietnamese",
  "Yoruba",
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

// Language mapping for UI (short codes to full names)
export const LANGUAGE_MAPPING: Record<string, LanguageCode> = Object.fromEntries(
  UI_LANGUAGES.map(({ locale, code }) => [locale, code]),
);

interface UIPreferencesState {
  // Theme State
  theme: ThemeMode;
  isDark: boolean;

  // Language State
  language: LanguageCode;

  // API Integration State
  isUpdatingTheme: boolean;
  isUpdatingLanguage: boolean;

  // Draft States (for form-based updates)
  draftTheme: ThemeMode | null;
  draftLanguage: LanguageCode | null;

  /** Sohbetteki ürün vitrininin yerleşimi — kullanıcı seçer, kalıcıdır */
  productShowcaseMode: ProductShowcaseMode;
}

/** strip: kaydırmalı şerit · focus: ortada büyük kart · grid: hepsi ızgarada */
export type ProductShowcaseMode = "strip" | "focus" | "grid";

interface UIPreferencesActions {
  // Theme Actions
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;

  setProductShowcaseMode: (mode: ProductShowcaseMode) => void;

  // Language Actions
  setLanguage: (language: LanguageCode) => void;

  // Combined Actions
  resetPreferences: () => void;

  // API Integration Actions
  initializeFromAPI: () => void;
  updateThemeOnAPI: (mode: ThemeMode) => Promise<void>;
  updateLanguageOnAPI: (language: LanguageCode) => Promise<void>;

  // Draft Actions (for form-based updates)
  setDraftTheme: (mode: ThemeMode | null) => void;
  setDraftLanguage: (language: LanguageCode | null) => void;
  saveDraftChanges: () => Promise<void>;
  resetDraftChanges: () => void;
}

// ===== Helper Functions =====
const getSystemTheme = (): boolean => {
  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }
  return false;
};

const updateThemeColors = (theme: ThemeMode) => {
  if (typeof window === "undefined") return;

  const root = document.documentElement;
  const isDark = theme === "dark" || (theme === "system" && getSystemTheme());
  const themeColors = isDark ? colors.dark : colors.light;

  // 1) Ham paleti CSS değişkeni olarak yaz (--color-{family}-{step})
  Object.entries(palette).forEach(([family, steps]) => {
    Object.entries(steps).forEach(([step, hex]) => {
      root.style.setProperty(`--color-${family}-${step}`, hex as string);
    });
  });

  // 2) Semantik token'ları yaz
  Object.entries(themeColors).forEach(([key, value]) => {
    const cssVar = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    root.style.setProperty(`--${cssVar}`, value);
  });

  // 3) 🔥 CRITICAL: Figma semantic colors'ı güncelle
  writeFigmaColorVars(root, isDark ? "dark" : "light");

  // 4) Tipografi değişkenlerini yaz
  writeTypographyVars(root, typography);

  // 5) Dark class'ını güncelle
  if (isDark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

const updateLanguage = (language: LanguageCode) => {
  i18n.changeLanguage(getUILocale(language));
};

const mapI18nCodeToLanguage = (code?: string): LanguageCode => {
  return getUILanguageName(code);
};

const getInitialLanguage = (): LanguageCode => {
  if (typeof window === "undefined") return "English";

  // Priority:
  // 1) i18next detected/resolved language (query/localStorage/navigator)
  // 2) browser language fallback
  const detectedLanguage =
    i18n.resolvedLanguage || i18n.language || navigator.language;

  return mapI18nCodeToLanguage(detectedLanguage);
};

// ===== Initial State =====
const initialState: UIPreferencesState = {
  theme: "light",
  isDark: false,
  language: getInitialLanguage(),
  isUpdatingTheme: false,
  isUpdatingLanguage: false,
  draftTheme: null,
  draftLanguage: null,
  productShowcaseMode: "strip",
};

// ===== UI Preferences Store =====
const useUIPreferencesStore = create<
  UIPreferencesState & UIPreferencesActions
>()(
  persist(
    (set, get) => ({
      // ===== State =====
      ...initialState,

      // ===== Theme Actions =====
      setTheme: (mode: ThemeMode) => {
        updateThemeColors(mode);
        const isDark =
          mode === "dark" || (mode === "system" && getSystemTheme());
        set({ theme: mode, isDark });
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const nextTheme = currentTheme === "light" ? "dark" : "light";
        get().setTheme(nextTheme);
      },

      setProductShowcaseMode: (mode) => set({ productShowcaseMode: mode }),

      // ===== Language Actions =====
      setLanguage: (language: LanguageCode) => {
        updateLanguage(language);
        set({ language });
      },

      // ===== Combined Actions =====
      resetPreferences: () => {
        updateThemeColors(initialState.theme);
        updateLanguage(initialState.language);
        set(initialState);
      },

      // ===== API Integration Actions =====
      initializeFromAPI: () => {
        const authStore = useAuthStore.getState();
        const user = authStore.user;

        if (user?.theme && typeof user.theme === "string") {
          const apiTheme = user.theme as ThemeMode;
          updateThemeColors(apiTheme);
          const isDark =
            apiTheme === "dark" || (apiTheme === "system" && getSystemTheme());
          set({ theme: apiTheme, isDark });
        }

        if (user?.language && typeof user.language === "string") {
          const apiLanguage = getLanguageFromURL() ?? getUILanguageName(user.language);
          updateLanguage(apiLanguage);
          set({ language: apiLanguage });
        }
      },

      updateThemeOnAPI: async (mode: ThemeMode) => {
        const authStore = useAuthStore.getState();
        const user = authStore.user;

        if (!user) {
          console.warn(
            "❌ [UI PREFERENCES] No user found, cannot update theme on API"
          );
          return;
        }

        set({ isUpdatingTheme: true });

        try {
          // Update local state immediately for better UX
          updateThemeColors(mode);
          const isDark =
            mode === "dark" || (mode === "system" && getSystemTheme());
          set({ theme: mode, isDark });

          // Update on API
          const { apiClient } = await import("@/api/config/api-client");
          const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");

          await apiClient.put(API_ENDPOINTS.updateProfile, {
            theme: mode,
          });

          // Refresh user data to sync with API (force refresh to bypass cache)
          await authStore.refreshUserData(true);
        } catch (error) {
          console.error(
            "❌ [UI PREFERENCES] Failed to update theme on API:",
            error
          );

          // Revert to previous state on error
          const currentUser = useAuthStore.getState().user;
          if (currentUser?.theme && typeof currentUser.theme === "string") {
            const previousTheme = currentUser.theme as ThemeMode;
            updateThemeColors(previousTheme);
            const isDark =
              previousTheme === "dark" ||
              (previousTheme === "system" && getSystemTheme());
            set({ theme: previousTheme, isDark });
          }
        } finally {
          set({ isUpdatingTheme: false });
        }
      },

      updateLanguageOnAPI: async (language: LanguageCode) => {
        const authStore = useAuthStore.getState();
        const user = authStore.user;

        if (!user) {
          console.warn(
            "❌ [UI PREFERENCES] No user found, cannot update language on API"
          );
          return;
        }

        set({ isUpdatingLanguage: true });

        try {
          // Update local state immediately for better UX
          updateLanguage(language);
          set({ language });

          // Update on API
          const { apiClient } = await import("@/api/config/api-client");
          const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");

          await apiClient.put(API_ENDPOINTS.updateProfile, {
            language: language,
          });

          // Refresh user data to sync with API (force refresh to bypass cache)
          await authStore.refreshUserData(true);
        } catch (error) {
          console.error(
            "❌ [UI PREFERENCES] Failed to update language on API:",
            error
          );

          // Revert to previous state on error
          const currentUser = useAuthStore.getState().user;
          if (
            currentUser?.language &&
            typeof currentUser.language === "string"
          ) {
            const previousLanguage = currentUser.language as LanguageCode;
            updateLanguage(previousLanguage);
            set({ language: previousLanguage });
          }
        } finally {
          set({ isUpdatingLanguage: false });
        }
      },

      // ===== Draft Actions (for form-based updates) =====
      setDraftTheme: (mode: ThemeMode | null) => {
        set({ draftTheme: mode });
      },

      setDraftLanguage: (language: LanguageCode | null) => {
        set({ draftLanguage: language });
      },

      saveDraftChanges: async () => {
        const { draftTheme, draftLanguage } = get();
        const authStore = useAuthStore.getState();
        const user = authStore.user;

        if (!user) {
          console.warn(
            "❌ [UI PREFERENCES] No user found, cannot save draft changes"
          );
          return;
        }

        set({ isUpdatingTheme: true, isUpdatingLanguage: true });

        try {
          const { apiClient } = await import("@/api/config/api-client");
          const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");

          const updateData: any = {};

          if (draftTheme !== null) {
            updateData.theme = draftTheme;
            // Update local state immediately for better UX
            updateThemeColors(draftTheme);
            const isDark =
              draftTheme === "dark" ||
              (draftTheme === "system" && getSystemTheme());
            set({ theme: draftTheme, isDark });
          }

          if (draftLanguage !== null) {
            updateData.language = draftLanguage;
            // Update local state immediately for better UX
            updateLanguage(draftLanguage);
            set({ language: draftLanguage });
          }

          // Send single API request with all changes
          if (Object.keys(updateData).length > 0) {
            await apiClient.put(API_ENDPOINTS.updateProfile, updateData);
          }

          // Refresh user data to sync with API (force refresh to bypass cache)
          await authStore.refreshUserData(true);

          // Clear draft changes
          set({ draftTheme: null, draftLanguage: null });
        } catch (error) {
          console.error(
            "❌ [UI PREFERENCES] Failed to save draft changes:",
            error
          );

          // Revert to previous state on error
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            if (
              draftTheme !== null &&
              currentUser.theme &&
              typeof currentUser.theme === "string"
            ) {
              const previousTheme = currentUser.theme as ThemeMode;
              updateThemeColors(previousTheme);
              const isDark =
                previousTheme === "dark" ||
                (previousTheme === "system" && getSystemTheme());
              set({ theme: previousTheme, isDark });
            }

            if (
              draftLanguage !== null &&
              currentUser.language &&
              typeof currentUser.language === "string"
            ) {
              const previousLanguage = currentUser.language as LanguageCode;
              updateLanguage(previousLanguage);
              set({ language: previousLanguage });
            }
          }
        } finally {
          set({ isUpdatingTheme: false, isUpdatingLanguage: false });
        }
      },

      resetDraftChanges: () => {
        set({ draftTheme: null, draftLanguage: null });
      },
    }),
    {
      name: "ui-preferences-storage",
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        productShowcaseMode: state.productShowcaseMode,
      }),
      onRehydrateStorage: () => (state) => {
        // Storage'dan yüklendiğinde preferences'ları uygula
        if (state) {
          updateThemeColors(state.theme);
          state.language = getLanguageFromURL() ?? getUILanguageName(state.language);
          updateLanguage(state.language);

          // isDark state'ini hesapla
          const isDark =
            state.theme === "dark" ||
            (state.theme === "system" && getSystemTheme());
          state.isDark = isDark;
        }
      },
    }
  )
);

// ===== System Theme Change Listener =====
if (typeof window !== "undefined") {
  // İlk yüklemede system tercihine göre varsayılan temayı uygula
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const store = useUIPreferencesStore.getState();
      if (store.theme === "system") {
        updateThemeColors("system");
        // isDark state'ini güncelle
        store.setTheme("system");
      }
    });
}

// ===== Convenience Hooks =====
export const useTheme = () => {
  const {
    theme,
    isDark,
    setTheme,
    toggleTheme,
    updateThemeOnAPI,
    isUpdatingTheme,
    draftTheme,
    setDraftTheme,
  } = useUIPreferencesStore();
  return {
    mode: theme,
    isDark,
    setTheme,
    toggleTheme,
    updateThemeOnAPI,
    isUpdatingTheme,
    draftTheme,
    setDraftTheme,
  };
};

export const useLanguage = () => {
  const {
    language,
    setLanguage,
    updateLanguageOnAPI,
    isUpdatingLanguage,
    draftLanguage,
    setDraftLanguage,
  } = useUIPreferencesStore();
  return {
    language,
    setLanguage,
    updateLanguageOnAPI,
    isUpdatingLanguage,
    draftLanguage,
    setDraftLanguage,
  };
};

// Draft Management Hook (for form-based updates)
export const useAppearanceDraft = () => {
  const {
    draftTheme,
    draftLanguage,
    setDraftTheme,
    setDraftLanguage,
    saveDraftChanges,
    resetDraftChanges,
    isUpdatingTheme,
    isUpdatingLanguage,
  } = useUIPreferencesStore();

  const hasDraftChanges = draftTheme !== null || draftLanguage !== null;
  const isSaving = isUpdatingTheme || isUpdatingLanguage;

  return {
    draftTheme,
    draftLanguage,
    setDraftTheme,
    setDraftLanguage,
    saveDraftChanges,
    resetDraftChanges,
    hasDraftChanges,
    isSaving,
  };
};

export default useUIPreferencesStore;

// ===== Export Types =====
export type { UIPreferencesState, UIPreferencesActions };
